import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { captureAndReturn } from 'utils/zod/captureAndReturn'

extendZodWithOpenApi(z)

const OverrideIconTypeSchema = z.enum(['bank', 'budget-approval'])
export type OverrideIconType = z.infer<typeof OverrideIconTypeSchema>

export const VisibleIfSchema = z
  .union([
    z.tuple([z.string().uuid()]), // [string]
    z.tuple([]), // []
  ])
  .catch(captureAndReturn('VisibleIfSchema'))

export type VisibleIf = z.infer<typeof VisibleIfSchema>

// 'document' covers both user-uploaded files and scraped URLs — both go through the document extraction pipeline
const AiSourceTypeSchema = z.enum(['document'])

const AiSourceInfoSchema = z.object({
  sourceType: AiSourceTypeSchema.optional(),
  quote: z.string().optional(),
  sourceLabel: z.string(),
  fileStoreId: z.string(),
  sourceUrl: z.string().optional(),
  pageNumber: z.number().optional(),
  heading: z.string().optional(),
  sheetName: z.string().optional(),
})

export const AiCompletionConfidenceSchema = z.enum(['high', 'medium', 'low'])
export type AiCompletionConfidence = z.infer<typeof AiCompletionConfidenceSchema>

const AiCompletionReferenceSchema = z.object({
  title: z.string(),
  url: z.string().url(),
})

const AiCompletionMetadataSchema = z
  .object({
    confidence: AiCompletionConfidenceSchema,
    reasoning: z.string(),
    references: z.array(AiCompletionReferenceSchema).optional(),
  })
  .openapi({
    description:
      'Confidence and reasoning for an answer submitted by the AI Form Completion block. Distinct from aiSources, which tracks document-extraction prefill citations.',
  })

export const FormAnswerMetadataSchema = z.object({
  isAiPrefilled: z.boolean().optional(),
  aiSources: z.array(AiSourceInfoSchema).optional(),
  aiCompletionMetadata: AiCompletionMetadataSchema.optional(),
})

export type FormAnswerMetadata = z.infer<typeof FormAnswerMetadataSchema>

export const BaseFormQuestionSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  description: z.string().optional(),
  visibleIf: VisibleIfSchema,
  isRequired: z.boolean(),
  readOnly: z.boolean().optional(),
  disabled: z.boolean().optional(), // NOTE: This is not set anywhere in the codebase, so I assume it's a legacy field
  weighting: z.number().optional(),
  tooltip: z.string().optional(),
  doubleEntryEnabled: z.boolean().optional(), // TODO(COR-358): Does this need to be in the base schema??
  overrideIconType: OverrideIconTypeSchema.optional(), // TODO(COR-358): Does this need to be in the base schema??
  isScorable: z.boolean().optional(), // Questions in rfx forms can be marked as scorable
  autoFlaggedScoreCategoryIds: z.array(z.string().uuid()).optional(),
  isMaterial: z.boolean().optional(), // Questions marked as material trigger change detection assessment flows
})

export const BaseFormAnswerSchema = z.object({
  questionId: z.string().nullable(),
  metadata: FormAnswerMetadataSchema.optional(),
})
